import React, { useState, useEffect } from 'react';
import axios from '../../axiosConfig';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
  InputLabel,
  FormControl,
  Select,
} from '@mui/material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './CreateBlog.css';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const BLOG_CATEGORIES = [
  'Technology',
  'Design',
  'Business',
  'Marketing',
  'AI',
  'Career',
  'Productivity',
  'Lifestyle',
  'Education',
  'Tutorials',
  'General',
];

const CreateBlog = () => {
  const navigate = useNavigate();
  const [title, setTitle] = useState('');
  const [excerpt, setExcerpt] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('Technology');
  const [status, setStatus] = useState('draft');
  const [tags, setTags] = useState('');
  const [image, setImage] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(!!localStorage.getItem('jwtToken'));
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      const timeout = setTimeout(() => {
        navigate('/login');
      }, 5000);

      return () => clearTimeout(timeout);
    }
  }, [isAuthenticated, navigate]);

  const handleFileChange = (event) => {
    const file = event.target.files[0];

    if (!file) return;

    const fileSizeInMB = file.size / (1024 * 1024);
    if (fileSizeInMB > 1) {
      toast.error('File size must be 1 MB or less.');
      return;
    }

    setImage(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!title.trim() || !content.trim()) {
      toast.error('Title and content are required.');
      return;
    }

    if (!excerpt.trim()) {
      toast.error('Please add a short blog excerpt.');
      return;
    }

    setIsSubmitting(true);

    const formData = new FormData();
    formData.append('title', title.trim());
    formData.append('excerpt', excerpt.trim());
    formData.append('content', content);
    formData.append('category', category);
    formData.append('status', status);
    formData.append('tags', tags);

    if (image) {
      formData.append('image', image);
    }

    try {
      const response = await axios.post('/api/blogs', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      toast.success('Blog created successfully!');
      navigate(`/blog/${response.data.blog._id}`);
    } catch (error) {
      console.error('Error creating blog:', error);
      toast.error(error?.response?.data?.message || 'Error creating blog. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <Container maxWidth="md" className="create-blog-page">
        <Paper className="create-blog-guard" elevation={0}>
          <Typography variant="h4" gutterBottom>
            You must be logged in to create a blog.
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Redirecting you to the login page in a few seconds...
          </Typography>
          <Button variant="contained" color="primary" onClick={() => navigate('/login')}>
            Go to Login
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" className="create-blog-page">
      <Paper className="create-blog-card" elevation={0}>
        <Box className="create-blog-header">
          <Typography variant="h3" className="page-title">
            Publish a new story
          </Typography>
          <Typography variant="body1" className="page-subtitle">
            Share knowledge, opinion, and insight with a strong editorial layout.
          </Typography>
        </Box>

        <form onSubmit={handleSubmit} className="create-blog-form">
          <Stack spacing={3}>
            <TextField
              label="Blog title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              fullWidth
              placeholder="How I built a scalable content engine"
            />

            <TextField
              label="Short excerpt"
              value={excerpt}
              onChange={(e) => setExcerpt(e.target.value)}
              required
              fullWidth
              multiline
              rows={2}
              placeholder="A short summary that appears in previews and cards."
              inputProps={{ maxLength: 180 }}
            />

            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={category}
                label="Category"
                onChange={(e) => setCategory(e.target.value)}
              >
                {BLOG_CATEGORIES.map((item) => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={status}
                label="Status"
                onChange={(e) => setStatus(e.target.value)}
              >
                <MenuItem value="draft">Draft</MenuItem>
                <MenuItem value="published">Published</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Tags"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              fullWidth
              placeholder="technology, product, growth"
            />

            <Box className="editor-wrap">
              <Typography variant="subtitle1" className="field-label">
                Content
              </Typography>
              <ReactQuill
                value={content}
                onChange={setContent}
                placeholder="Write your story here..."
                className="editor"
              />
            </Box>

            <Box className="upload-box">
              <Typography variant="subtitle1" className="field-label">
                Feature image
              </Typography>
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="image-input"
              />
              <Typography variant="caption" className="upload-hint">
                Recommended size: 1200x700 px • Max 1MB
              </Typography>
            </Box>

            <Button
              type="submit"
              variant="contained"
              size="large"
              className="submit-button"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Publishing...' : status === 'published' ? 'Publish story' : 'Save as draft'}
            </Button>
          </Stack>
        </form>
      </Paper>
    </Container>
  );
};

export default CreateBlog;
